import { Injectable, Injector } from '@angular/core';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, of, from } from 'rxjs';
import { catchError, timeout, retry, switchMap } from 'rxjs/operators';
import { LogRecord } from '../models';
import { environment } from 'src/environments/environment';
import { firstValueFrom } from 'rxjs';
import { CLOUDWATCH_ENDPOINTS, LOG_GROUP_NAMES } from '../constants';

// Import Buffer for base64 decoding (similar to existing services)
const { Buffer } = require('buffer');

/**
 * Error types for classification and handling
 */
export enum WriteLoggingErrorType {
  NETWORK_ERROR = 'NETWORK_ERROR',
  AUTHENTICATION_ERROR = 'AUTH_ERROR',
  PAYLOAD_TOO_LARGE = 'PAYLOAD_TOO_LARGE',
  RATE_LIMIT = 'RATE_LIMIT',
  SERVER_ERROR = 'SERVER_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR'
}

/**
 * Response interface for log transmission
 */
export interface WriteLoggingResponse {
  success: boolean;
  message?: string;
  errorType?: WriteLoggingErrorType;
  statusCode?: number;
}

/**
 * Configuration interface for WriteLoggingService
 */
export interface WriteLoggingConfig {
  /** Request timeout in milliseconds */
  timeoutMs: number;
  /** Maximum retry attempts for failed requests */
  maxRetries: number;
  /** Enable request/response logging for debugging */
  enableDebugLogging: boolean;
}

/**
 * Default configuration for WriteLoggingService
 */
const DEFAULT_CONFIG: WriteLoggingConfig = {
  timeoutMs: 10000, // 10 seconds
  maxRetries: 2,
  enableDebugLogging: true
};

/**
 * WriteLoggingService handles CloudWatch integration by transmitting logs
 * to the backend CloudWatch API endpoints. Uses the existing pattern with
 * session-based log streams and environment-specific log groups.
 */
@Injectable({
  providedIn: 'root'
})
export class WriteLoggingService {
  private config: WriteLoggingConfig;
  private injector: Injector;
  private http: HttpClient | null = null;
  private amplifyService: any = null;
  private corsWarningShown = false;
  private amplifyWarningShown = false;
  private readonly DEVELOPMENT_MODE = !environment.production;
  
  // Circuit breaker with cooloff and throttling
  private failureCount = 0;
  private lastFailureTime = 0;
  private retryAttempts = 0;
  private circuitState: 'CLOSED' | 'OPEN' | 'HALF_OPEN' | 'COOLOFF' = 'CLOSED';
  private nextRetryTime = 0;
  private lastRequestTime = 0;
  private cooloffStartTime = 0;
  
  // Circuit breaker configuration
  private readonly MAX_FAILURES = 3; // Reduced to 3 for faster detection
  private readonly RETRY_DELAY = 5 * 60 * 1000; // 5 minutes
  private readonly MAX_RETRY_ATTEMPTS = 3; // Try 3 times every 5 minutes
  private readonly REQUEST_THROTTLE_MS = 5000; // 5 seconds between requests
  private readonly COOLOFF_DURATION = 30 * 1000; // 30 seconds cooloff after failures
  private readonly NETWORK_ERROR_COOLOFF = 60 * 1000; // 1 minute for network errors
  
  // Request batching and throttling
  private pendingLogs: LogRecord[] = [];
  private batchTimer: any = null;
  private readonly BATCH_SIZE = 10; // Send up to 10 logs per request
  private readonly BATCH_TIMEOUT = 5000; // Send batch after 5 seconds
  
  // Prevent infinite loops by tracking processing state
  private isProcessingRequest = false;
  private static globalProcessingFlag = false;
  
  // Global request counter to detect infinite loops
  private static requestCounter = 0;
  private static lastResetTime = Date.now();
  private readonly MAX_REQUESTS_PER_MINUTE = 10;
  
  // Emergency shutdown flag
  private static nuclearShutdown = false;
  private httpOptions = {
    headers: new HttpHeaders({ 
      'Content-Type': 'application/json',
      'Accept': 'application/json'
    })
  };

  // Current log stream
  currentLogStream: any;

  constructor(injector: Injector) {
    this.injector = injector;
    this.config = { ...DEFAULT_CONFIG };
    
    // Enable debug logging in development
    this.config.enableDebugLogging = this.isDevelopmentMode();
  }

  /**
   * Set stream
   * @param stream The log stream to use
   */
  setStream(stream: any): void {
    this.currentLogStream = stream;
  }


  /**
   * Write multiple log records to CloudWatch with cooloff and throttling
   * 
   * @param logRecords Array of log records to transmit
   * @returns Promise resolving to WriteLoggingResponse
   */
  async writeLogsBatch(logRecords: LogRecord[]): Promise<WriteLoggingResponse> {
    // Multiple layers of infinite loop prevention
    
    // Check for shutdown mode
    if (WriteLoggingService.nuclearShutdown) {
      return {
        success: false,
        message: 'WriteLoggingService is in nuclear shutdown mode'
      };
    }
    
    // Layer 1: Instance-level processing guard
    if (this.isProcessingRequest) {
      // Add to pending logs for later processing
      this.addToPendingLogs(logRecords);
      return {
        success: false,
        message: 'Request queued - processing in progress'
      };
    }

    // Layer 2: Global processing guard (across all instances)
    if (WriteLoggingService.globalProcessingFlag) {
      this.addToPendingLogs(logRecords);
      return {
        success: false,
        message: 'Request queued - global processing in progress'
      };
    }

    // Circuit breaker check
    const circuitStatus = this.checkEnhancedCircuitBreaker();
    if (!circuitStatus.canProceed) {
      this.fallbackToConsoleLogging(logRecords);
      return {
        success: false,
        message: circuitStatus.message,
        errorType: WriteLoggingErrorType.NETWORK_ERROR
      };
    }

    // Layer 4: Request Throttling (5 second minimum between requests)
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.REQUEST_THROTTLE_MS) {
      // Add to pending logs and schedule for later
      this.addToPendingLogs(logRecords);
      this.scheduleBatchTransmission();
      return {
        success: false,
        message: `Request throttled - will retry in ${Math.ceil((this.REQUEST_THROTTLE_MS - timeSinceLastRequest) / 1000)} seconds`
      };
    }

    // Set all processing flags
    this.isProcessingRequest = true;
    WriteLoggingService.globalProcessingFlag = true;
    this.lastRequestTime = now;

    try {
      if (!logRecords || logRecords.length === 0) {
        return {
          success: true,
          message: 'No logs to transmit'
        };
      }

      // Always check authentication, but handle failures gracefully
      const isAuthenticated = await this.checkUserAuthentication();
      if (!isAuthenticated) {
        // Fallback to console logging when user not authenticated
        this.fallbackToConsoleLogging(logRecords);
        
        return {
          success: false,
          message: 'CloudWatch transmission skipped - user not authenticated'
        };
      }

      // Send logs with error handling
      const result = await this.sendLogsWithRetry(logRecords);
      
      if (result.success) {
        // Record success for circuit breaker
        this.recordSuccess();
        
        // Process any pending logs if circuit is now healthy
        this.processPendingLogs();
      } else {
        // Record failure and enter cooloff if needed
        this.recordFailure(result.errorType);
        
        // Fallback to console logging
        this.fallbackToConsoleLogging(logRecords);
      }

      return result;

    } catch (error) {
      // Record failure for circuit breaker
      this.recordFailure(this.classifyError(error));
      
      const errorResponse = this.handleTransmissionError(error, `batch of ${logRecords.length} logs`);
      
      // Fallback to console logging when API is unavailable
      this.fallbackToConsoleLogging(logRecords);
      
      return errorResponse;
    } finally {
      // Always reset ALL processing flags
      this.isProcessingRequest = false;
      WriteLoggingService.globalProcessingFlag = false;
    }
  }

  /**
   * Update service configuration
   * 
   * @param config Partial configuration to update
   */
  updateConfig(config: Partial<WriteLoggingConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current service configuration
   * 
   * @returns Current WriteLoggingConfig
   */
  getConfig(): WriteLoggingConfig {
    return { ...this.config };
  }

  /**
   * Check if the service is available and healthy
   */
  async checkServiceHealth(): Promise<boolean> {
    try {
      const apiUrl = this.getApiUrl();
      if (!apiUrl) {
        return false;
      }

      const headers = await this.buildHttpHeaders();
      
      // Simple health check - try to get auth token
      const token = await this.getAccessToken();
      return token !== null;

    } catch (error) {
      return false;
    }
  }

  /**
   * Send CloudWatch request with authentication and error handling
   */
  private async sendCloudWatchRequest(endpoint: string, params: any): Promise<any> {
    // Lazy initialize dependencies to avoid circular dependencies
    if (!this.http) {
      this.http = this.injector.get(HttpClient);
    }

    const apiUrl = this.getApiUrl();
    if (!apiUrl) {
      throw new Error('API URL not available');
    }

    const fullUrl = `${apiUrl}${endpoint}`;
    
    // Get auth token and build headers 
    const token = await this.getAccessToken();
    let headers = this.httpOptions.headers;
    if (token) {
      headers = headers.append('Authorization', token);
    }

    return firstValueFrom(
      this.http.post(fullUrl, params, { headers }).pipe(
        timeout(this.config.timeoutMs),
        retry(this.config.maxRetries),
        catchError((error: HttpErrorResponse) => {
          // Handle CORS errors gracefully - these should trigger immediate circuit breaker
          if (
            error.status === 0 ||
            error.statusText === 'Unknown Error' ||
            (error.message && error.message.toLowerCase().includes('cors'))
          ) {
            // CORS errors indicate a configuration issue, trigger longer cooloff
            this.recordFailure(WriteLoggingErrorType.NETWORK_ERROR);
            this.corsWarningShown = true;

            this.internalLog(
              `CORS error detected - entering extended cooloff: ${error.status}`
            );

            return throwError(() => error);
          }

          // Record failure for circuit breaker with proper error classification
          this.recordFailure(this.classifyError(error));

          // Error logging removed to prevent infinite loops
          if (this.config.enableDebugLogging) {
            this.internalLog(`CloudWatch request failed: ${error.status}`);
          }

          return throwError(() => error);
        })
      )
    );
  }

  /**
   * Get authorization token for CloudWatch requests
   * @description Gets the current access token without triggering console logs
   * @returns Promise with token or null
   */
  private async getAccessToken(): Promise<string | null> {
    try {
      // Lazy initialize AmplifyService to avoid circular dependencies
      if (!this.amplifyService) {
        try {
          // Try dynamic import for proper injection
          const AmplifyServiceModule = await import('../../services/amplify.service');
          this.amplifyService = this.injector.get(AmplifyServiceModule.AmplifyService);
        } catch (error) {
          // AmplifyService not available
        }
      }

      if (!this.amplifyService) {
        this.amplifyWarningShown = true;
        return null;
      }

      const result: any = await this.amplifyService.getAccessToken();
      if (result && result.statusFlag && result.accessToken) {
        return result.accessToken;
      }
      return null;

    } catch (error) {
      return null;
    }
  }

  /**
   * Get API URL based on environment configuration
   */
  private getApiUrl(): string | null {
    try {
      return Buffer.from(environment.environmentURL, 'base64').toString();
    } catch (error) {
      return null;
    }
  }

  /**
   * Get CloudWatch log group name based on environment
   * 
   * @returns Log group name
   */
  getLogGroupName(): string {
    if (environment.production) {
      return LOG_GROUP_NAMES.PRODUCTION;
    } else if (environment.test) {
      return LOG_GROUP_NAMES.TEST;
    } else if (environment.dev) {
      return LOG_GROUP_NAMES.DEVELOPMENT;
    }
    // Default to dev if no environment flag is set
    return LOG_GROUP_NAMES.DEVELOPMENT;
  }

  /**
   * Group log records by session ID for batch transmission
   * 
   * @param logRecords Array of log records
   * @returns Object with session IDs as keys and log arrays as values
   */
  private groupLogsBySession(logRecords: LogRecord[]): Record<string, LogRecord[]> {
    return logRecords.reduce((acc, log) => {
      const sessionId = log.kinective.sessionId;
      if (!acc[sessionId]) {
        acc[sessionId] = [];
      }
      acc[sessionId].push(log);
      return acc;
    }, {} as Record<string, LogRecord[]>);
  }

  /**
   * Build HTTP headers with authentication and content type
   * 
   * @returns Promise resolving to HttpHeaders
   */
  private async buildHttpHeaders(): Promise<HttpHeaders> {
    let headers = this.httpOptions.headers;

    try {
      // Get Amplify access token for authentication
      const accessToken = await this.getAccessToken();
      if (accessToken) {
        headers = headers.set('Authorization', accessToken);
      }
    } catch (error) {
      // Continue without authentication - will be handled by error handling
    }

    return headers;
  }

  /**
   * Handle transmission errors with classification and fallback
   * 
   * @param error The error that occurred
   * @param context Context description for logging
   * @returns WriteLoggingResponse with error details
   */
  private handleTransmissionError(error: any, context: string): WriteLoggingResponse {
    const errorType = this.classifyError(error);
    const statusCode = error?.status || error?.statusCode;

    // Use internal logging to prevent infinite loops
    if (this.config.enableDebugLogging) {
      this.internalLog(`Failed to transmit ${context}: ${errorType} (${statusCode})`);
    }

    // Provide specific error messages based on error type
    let message: string;
    switch (errorType) {
      case WriteLoggingErrorType.AUTHENTICATION_ERROR:
        message = 'Authentication failed - CloudWatch logging may be disabled';
        break;
      case WriteLoggingErrorType.PAYLOAD_TOO_LARGE:
        message = 'Log payload too large - logs will be dropped';
        break;
      case WriteLoggingErrorType.RATE_LIMIT:
        message = 'Rate limit exceeded - will retry with backoff';
        break;
      case WriteLoggingErrorType.NETWORK_ERROR:
        message = 'Network error - will retry transmission';
        break;
      case WriteLoggingErrorType.SERVER_ERROR:
        message = 'Server error - will retry transmission';
        break;
      default:
        message = 'Unknown error occurred during log transmission';
    }

    return {
      success: false,
      message,
      errorType,
      statusCode
    };
  }

  /**
   * Classify error type for appropriate handling
   * 
   * @param error The error to classify
   * @returns WriteLoggingErrorType classification
   */
  private classifyError(error: any): WriteLoggingErrorType {
    if (!error) {
      return WriteLoggingErrorType.UNKNOWN_ERROR;
    }

    const errorMessage = error.message?.toLowerCase() || '';
    const statusCode = error.status || error.statusCode;

    // CORS errors (status 0 with specific messages)
    if (statusCode === 0 || errorMessage.includes('cors') || 
        errorMessage.includes('access-control') || 
        errorMessage.includes('preflight') ||
        errorMessage.includes('blocked by cors policy')) {
      return WriteLoggingErrorType.NETWORK_ERROR;
    }

    // HTTP status code classification
    if (statusCode === 413 || errorMessage.includes('payload too large')) {
      return WriteLoggingErrorType.PAYLOAD_TOO_LARGE;
    }

    if (statusCode === 401 || statusCode === 403 || 
        errorMessage.includes('unauthorized') || errorMessage.includes('forbidden')) {
      return WriteLoggingErrorType.AUTHENTICATION_ERROR;
    }

    if (statusCode === 429 || errorMessage.includes('rate limit')) {
      return WriteLoggingErrorType.RATE_LIMIT;
    }

    if (statusCode >= 500 || errorMessage.includes('server error')) {
      return WriteLoggingErrorType.SERVER_ERROR;
    }

    // Network-related errors (including net::ERR_FAILED)
    if (errorMessage.includes('network') || 
        errorMessage.includes('connection') || 
        errorMessage.includes('timeout') ||
        errorMessage.includes('unreachable') ||
        errorMessage.includes('net::err') ||
        errorMessage.includes('failed to fetch')) {
      return WriteLoggingErrorType.NETWORK_ERROR;
    }

    return WriteLoggingErrorType.UNKNOWN_ERROR;
  }

  /**
   * Fallback to console logging when CloudWatch API is unavailable
   * 
   * @param logRecords Array of log records to output to console
   */
  private fallbackToConsoleLogging(logRecords: LogRecord[]): void {
    if (!logRecords || logRecords.length === 0) {
      return;
    }

    // Instead, use internal logging for debugging only
    this.internalLog(`CloudWatch failed, ${logRecords.length} logs would fallback to console`);
  }

  /**
   * Get appropriate console method based on log level
   * 
   * @param level Log level string
   * @returns Console method function
   */
  private getConsoleMethod(level: string): (...args: any[]) => void {
    switch (level) {
      case 'error':
        return console.error.bind(console);
      case 'warn':
        return console.warn.bind(console);
      case 'info':
        return console.info.bind(console);
      case 'debug':
        return console.debug.bind(console);
      default:
        return console.log.bind(console);
    }
  }

  /**
   * Write log (compatible with existing WriteLoggingService)
   * @param log The log data to write
   * @returns Observable for compatibility
   */
  writeLog(log: any): Observable<any> {
    const params = {
      logStream: this.currentLogStream,
      logGroupName: 'gcadmininternal',
      log: log
    };

    return this.sendCloudWatchRequestObservable(CLOUDWATCH_ENDPOINTS.SINGLE_LOG, params);
  }

  /**
   * Write user log (compatible with existing WriteLoggingService)
   * @param log The user log data to write
   * @returns Observable for compatibility
   */
  writeUserLog(log: any): Observable<any> {
    const params = {
      logStream: this.currentLogStream,
      logGroupName: 'gcuserlogs',
      log: log
    };

    return this.sendCloudWatchRequestObservable(CLOUDWATCH_ENDPOINTS.SINGLE_LOG, params);
  }

  /**
   * Write structured log batch (compatible with LogQueueManager)
   * @param logs Array of logs with session IDs
   * @returns Observable for compatibility
   */
  writeStructuredLogBatch(logs: Array<{ record: LogRecord; sessionId: string }>): Observable<any> {
    if (!logs || logs.length === 0) {
      return of(null);
    }

    // Process each log individually

    // Send individual requests for each log
    const requests = logs.map(log => {
      const params = {
        logStream: log.sessionId,
        logGroupName: this.getLogGroupName(),
        log: log.record
      };
      return this.sendCloudWatchRequestObservable(CLOUDWATCH_ENDPOINTS.SINGLE_LOG, params);
    });

    // Return the first request for compatibility (or combine them)
    return requests.length > 0 ? requests[0] : of(null);
  }

  /**
   * Send CloudWatch request and return Observable (for compatibility)
   * @param endpoint The CloudWatch endpoint
   * @param params The request parameters
   * @returns Observable
   */
  private sendCloudWatchRequestObservable(endpoint: string, params: any): Observable<any> {

    // Lazy initialize dependencies
    if (!this.http) {
      this.http = this.injector.get(HttpClient);
    }

    const apiUrl = this.getApiUrl();
    if (!apiUrl) {
      return throwError(() => new Error('CloudWatch API not available'));
    }

    const fullUrl = `${apiUrl}${endpoint}`;

    // Get auth token and make request
    return from(this.getAccessToken()).pipe(
      switchMap(token => {
        // Clone headers and add Authorization if token exists (like example)
        let headers = this.httpOptions.headers;
        if (token) {
          headers = headers.append('Authorization', token);
        }
        return this.http!.post(fullUrl, params, { headers }).pipe(
          timeout(this.config.timeoutMs),
          retry(this.config.maxRetries),
          catchError((error: HttpErrorResponse) => {
            if (this.config.enableDebugLogging) {
              this.internalLog(`CloudWatch request failed: ${error.status} - ${fullUrl}`);
            }
            return throwError(() => error); 
          })
        );
      })
    );
  }

  /**
   * Check if running in development mode
   * 
   * @returns True if in development mode
   */
  private isDevelopmentMode(): boolean {
    // Use Angular environment configuration
    return !environment.production;
  }

  /**
   * Add logs to pending queue for later processing
   */
  private addToPendingLogs(logRecords: LogRecord[]): void {
    // Limit pending logs to prevent memory issues
    const maxPendingLogs = 100;
    const availableSpace = maxPendingLogs - this.pendingLogs.length;
    
    if (availableSpace > 0) {
      const logsToAdd = logRecords.slice(0, availableSpace);
      this.pendingLogs.push(...logsToAdd);
      
      if (logRecords.length > availableSpace) {
        this.internalLog(`Pending logs queue full, dropped ${logRecords.length - availableSpace} logs`);
      }
    }
  }

  /**
   * Schedule batch transmission of pending logs
   */
  private scheduleBatchTransmission(): void {
    if (this.batchTimer) {
      return; // Already scheduled
    }
    
    const timeUntilNextRequest = this.REQUEST_THROTTLE_MS - (Date.now() - this.lastRequestTime);
    const delay = Math.max(timeUntilNextRequest, 1000); // At least 1 second
    
    this.batchTimer = setTimeout(() => {
      this.processPendingLogs();
      this.batchTimer = null;
    }, delay);
  }

  /**
   * Process pending logs if circuit breaker allows
   */
  private async processPendingLogs(): Promise<void> {
    if (this.pendingLogs.length === 0 || this.isProcessingRequest) {
      return;
    }
    
    const circuitStatus = this.checkEnhancedCircuitBreaker();
    if (!circuitStatus.canProceed) {
      return;
    }
    
    // Take a batch of pending logs
    const batchSize = Math.min(this.BATCH_SIZE, this.pendingLogs.length);
    const batch = this.pendingLogs.splice(0, batchSize);
    
    try {
      await this.writeLogsBatch(batch);
    } catch (error) {
      // If processing fails, put logs back at the front
      this.pendingLogs.unshift(...batch);
    }
  }

  /**
   * Enhanced Circuit Breaker with Cooloff States
   */
  private checkEnhancedCircuitBreaker(): { canProceed: boolean; message: string } {
    const now = Date.now();
    
    switch (this.circuitState) {
      case 'CLOSED':
        // Normal operation - allow requests
        return { canProceed: true, message: 'Circuit closed - normal operation' };
        
      case 'COOLOFF':
        // In cooloff period after failures
        const cooloffRemaining = this.cooloffStartTime + this.COOLOFF_DURATION - now;
        if (cooloffRemaining <= 0) {
          // Cooloff period ended, try half-open
          this.circuitState = 'HALF_OPEN';
          return { canProceed: true, message: 'Cooloff ended - testing connection' };
        }
        return { 
          canProceed: false, 
          message: `In cooloff period - ${Math.ceil(cooloffRemaining / 1000)} seconds remaining` 
        };
        
      case 'OPEN':
        // Circuit is open - check if it's time to retry
        if (now >= this.nextRetryTime) {
          if (this.retryAttempts < this.MAX_RETRY_ATTEMPTS) {
            this.circuitState = 'HALF_OPEN';
            return { canProceed: true, message: 'Attempting retry - testing connection' };
          } else {
            // Reset retry cycle after max attempts
            this.retryAttempts = 0;
            this.nextRetryTime = now + this.RETRY_DELAY;
            return { 
              canProceed: false, 
              message: `Max retries exceeded - waiting ${Math.ceil(this.RETRY_DELAY / 60000)} minutes` 
            };
          }
        }
        const retryRemaining = Math.ceil((this.nextRetryTime - now) / 60000);
        return { 
          canProceed: false, 
          message: `Circuit open - retry in ${retryRemaining} minutes` 
        };
        
      case 'HALF_OPEN':
        // Testing state - allow limited requests
        return { canProceed: true, message: 'Half-open - testing connection' };
        
      default:
        return { canProceed: true, message: 'Unknown state - allowing request' };
    }
  }

  /**
   * Send logs with enhanced retry logic
   */
  private async sendLogsWithRetry(logRecords: LogRecord[]): Promise<WriteLoggingResponse> {
    try {
      // Send each log individually using the single endpoint
      const promises = logRecords.map(async (logRecord) => {
        const params = {
          logStream: logRecord.kinective.sessionId,
          logGroupName: this.getLogGroupName(),
          log: logRecord
        };
        return this.sendCloudWatchRequest(CLOUDWATCH_ENDPOINTS.SINGLE_LOG, params);
      });

      await Promise.all(promises);

      return {
        success: true,
        message: `${logRecords.length} log records transmitted successfully`
      };

    } catch (error: any) {
      const errorType = this.classifyError(error);
      
      return {
        success: false,
        message: `Failed to transmit logs: ${error?.message || 'Unknown error'}`,
        errorType: errorType,
        statusCode: error?.status || error?.statusCode
      };
    }
  }

  /**
   * Smart Circuit Breaker - checks if CloudWatch requests should be blocked
   */
  private isCircuitBreakerOpen(): boolean {
    const now = Date.now();
    
    switch (this.circuitState) {
      case 'CLOSED':
        // Normal operation - allow requests
        return false;
        
      case 'OPEN':
        // Circuit is open - check if it's time to retry
        if (now >= this.nextRetryTime) {
          // Time to attempt retry
          if (this.retryAttempts < this.MAX_RETRY_ATTEMPTS) {
            this.circuitState = 'HALF_OPEN';
            return false; // Allow test request
          } else {
            // Reset retry cycle after 3 attempts
            this.retryAttempts = 0;
            this.nextRetryTime = now + this.RETRY_DELAY;
            return true; // Still blocked, wait another 5 minutes
          }
        }
        return true; // Still in open state, block requests
        
      case 'HALF_OPEN':
        // Testing state - allow limited requests
        return false;
        
      default:
        return false;
    }
  }

  /**
   * Record a successful CloudWatch request
   */
  private recordSuccess(): void {
    this.failureCount = 0;
    this.retryAttempts = 0;
    this.circuitState = 'CLOSED';
    // Internal log for debugging
    if (this.circuitState !== 'CLOSED') {
      this.internalLog('Circuit breaker: CloudWatch connection restored');
    }
  }

  /**
   * Record a failed CloudWatch request with enhanced cooloff logic
   */
  private recordFailure(errorType?: WriteLoggingErrorType): void {
    this.failureCount++;
    this.lastFailureTime = Date.now();
    
    // Determine cooloff duration based on error type
    let cooloffDuration = this.COOLOFF_DURATION;
    if (errorType === WriteLoggingErrorType.NETWORK_ERROR) {
      cooloffDuration = this.NETWORK_ERROR_COOLOFF; // Longer cooloff for network errors
    }
    
    if (this.circuitState === 'HALF_OPEN') {
      // Test failed, enter cooloff before going to open state
      this.retryAttempts++;
      this.circuitState = 'COOLOFF';
      this.cooloffStartTime = Date.now();
      this.internalLog(`Circuit breaker: Retry ${this.retryAttempts}/${this.MAX_RETRY_ATTEMPTS} failed, entering cooloff for ${cooloffDuration / 1000} seconds`);
      
      // Schedule transition to OPEN state after cooloff
      setTimeout(() => {
        if (this.circuitState === 'COOLOFF') {
          this.circuitState = 'OPEN';
          this.nextRetryTime = Date.now() + this.RETRY_DELAY;
        }
      }, cooloffDuration);
      
    } else if (this.failureCount >= this.MAX_FAILURES) {
      // Too many failures, enter cooloff before opening circuit
      this.circuitState = 'COOLOFF';
      this.cooloffStartTime = Date.now();
      this.retryAttempts = 0;
      this.internalLog(`Circuit breaker: ${this.MAX_FAILURES} failures detected, entering cooloff for ${cooloffDuration / 1000} seconds`);
      
      // Schedule transition to OPEN state after cooloff
      setTimeout(() => {
        if (this.circuitState === 'COOLOFF') {
          this.circuitState = 'OPEN';
          this.nextRetryTime = Date.now() + this.RETRY_DELAY;
        }
      }, cooloffDuration);
    }
  }

  /**
   * Internal logging method to avoid infinite loops
   */
  private internalLog(message: string): void {
    try {
      if (typeof window !== 'undefined' && (window as any).loggingService?.originalConsole?.log) {
        (window as any).loggingService.originalConsole.log(`[CLOUDWATCH_CIRCUIT_BREAKER] ${message}`);
      }
    } catch (error) {
      // Silent failure
    }
  }

  /**
   * Public method to get enhanced circuit breaker status (for debugging)
   */
  getCircuitBreakerStatus(): any {
    const now = Date.now();
    const cooloffRemaining = this.circuitState === 'COOLOFF' ? 
      Math.max(0, this.cooloffStartTime + this.COOLOFF_DURATION - now) : 0;
    
    const status = {
      state: this.circuitState,
      failureCount: this.failureCount,
      retryAttempts: this.retryAttempts,
      nextRetryTime: this.nextRetryTime ? new Date(this.nextRetryTime).toISOString() : null,
      minutesUntilRetry: this.nextRetryTime ? Math.ceil((this.nextRetryTime - Date.now()) / 60000) : 0,
      cooloffRemaining: Math.ceil(cooloffRemaining / 1000),
      lastRequestTime: this.lastRequestTime ? new Date(this.lastRequestTime).toISOString() : null,
      timeSinceLastRequest: this.lastRequestTime ? Math.ceil((now - this.lastRequestTime) / 1000) : 0,
      canMakeRequest: (now - this.lastRequestTime) >= this.REQUEST_THROTTLE_MS,
      pendingLogsCount: this.pendingLogs.length,
      maxFailures: this.MAX_FAILURES,
      maxRetryAttempts: this.MAX_RETRY_ATTEMPTS,
      retryDelayMinutes: this.RETRY_DELAY / 60000,
      requestThrottleSeconds: this.REQUEST_THROTTLE_MS / 1000,
      cooloffDurationSeconds: this.COOLOFF_DURATION / 1000,
      // Nuclear shutdown status
      nuclearShutdown: WriteLoggingService.nuclearShutdown,
      requestCount: WriteLoggingService.requestCounter,
      globalProcessing: WriteLoggingService.globalProcessingFlag
    };
    
    this.internalLog(`Enhanced Circuit Breaker Status: ${JSON.stringify(status, null, 2)}`);
    return status;
  }

  /**
   * Clear pending logs and reset timers
   */
  clearPendingLogs(): void {
    this.pendingLogs = [];
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
      this.batchTimer = null;
    }
    this.internalLog('Pending logs cleared');
  }

  /**
   * Force reset circuit breaker to closed state
   */
  resetCircuitBreaker(): void {
    this.circuitState = 'CLOSED';
    this.failureCount = 0;
    this.retryAttempts = 0;
    this.nextRetryTime = 0;
    this.cooloffStartTime = 0;
    this.lastRequestTime = 0;
    this.clearPendingLogs();
    this.internalLog('Circuit breaker reset to CLOSED state');
  }

  /**
   * Completely disable WriteLoggingService
   */
  static nuclearShutdownActivate(): void {
    WriteLoggingService.nuclearShutdown = true;
    WriteLoggingService.globalProcessingFlag = false;
    WriteLoggingService.requestCounter = 0;
    
    // Use original console to avoid loops
    if (typeof window !== 'undefined' && (window as any).loggingService?.originalConsole?.error) {
      (window as any).loggingService.originalConsole.error('[NUCLEAR_SHUTDOWN] WriteLoggingService completely disabled');
    }
  }

  /**
   * Reset nuclear shutdown and circuit breaker (use with caution)
   */
  static nuclearShutdownReset(): void {
    WriteLoggingService.nuclearShutdown = false;
    WriteLoggingService.globalProcessingFlag = false;
    WriteLoggingService.requestCounter = 0;
    WriteLoggingService.lastResetTime = Date.now();
    
    // Use original console to avoid loops
    if (typeof window !== 'undefined' && (window as any).loggingService?.originalConsole?.log) {
      (window as any).loggingService.originalConsole.log('[NUCLEAR_SHUTDOWN] WriteLoggingService re-enabled');
    }
  }

  /**
   * Check if user is authenticated using AmplifyService
   * 
   * @returns Promise resolving to authentication status
   */
  private async checkUserAuthentication(): Promise<boolean> {
    try {
      // Lazy initialize AmplifyService to avoid circular dependencies
      if (!this.amplifyService) {
        try {
          const AmplifyServiceModule = await import('../../services/amplify.service');
          this.amplifyService = this.injector.get(AmplifyServiceModule.AmplifyService);
        } catch (error) {
          if (this.config.enableDebugLogging) {
            this.internalLog('Failed to load AmplifyService');
          }
          return false;
        }
      }

      if (!this.amplifyService || typeof this.amplifyService.getCurrentUser !== 'function') {
        return false;
      }

      // Check if user is authenticated
      const userResult = await this.amplifyService.getCurrentUser();
      return !!(userResult && userResult.success);

    } catch (error) {
      if (this.config.enableDebugLogging) {
        this.internalLog('Authentication check failed');
      }
      return false;
    }
  }
}